import React, { Component } from 'react';

import appSettings from '../../config/settings';

import { LoadableComponentErrorBoundaryPage } from './LoadableComponentErrorBoundaryPage';

// Use ErrorBoundary to catch ChunkLoadError
// https://reactjs.org/docs/error-boundaries.html
class LoadableComponentErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { error };
  }

  componentDidCatch(error, info) {
    // Log so we can see the real error message in production. Without this, all
    // we see is the React invariant after the throw cascades.
    // eslint-disable-next-line no-console
    console.error('[LoadableErrorBoundary]', error?.message || error, info?.componentStack);
  }

  render() {
    const { error } = this.state;
    if (error && error.name === 'ChunkLoadError') {
      return <LoadableComponentErrorBoundaryPage />;
    }

    // CRITICAL: previously this returned `this.props.children` for any non-chunk
    // error, which immediately re-threw the same render error → infinite loop
    // → 4000+ console errors, frozen browser, "Loading search results…" stuck.
    // For unhandled errors, render a minimal fallback so the tree stops cycling.
    if (error) {
      return (
        <div style={{ padding: '24px', fontFamily: 'sans-serif', color: '#444' }}>
          <h2 style={{ marginTop: 0 }}>Something went wrong loading this page.</h2>
          <p>
            Please reload the page. If the problem persists, contact support with the error
            details from the browser console.
          </p>
          {error?.message ? (
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{String(error.message)}</pre>
          ) : null}
        </div>
      );
    }

    return this.props.children;
  }
}

// LoadableComponentErrorBoundary helps in situations
// where production build changes in the server and
// long-living client app tries to fetch code chunks that don't exist anymore.
// Note: in development mode with Hot Module Reloading (HMR) in use, this causes error loops.
const UseLoadableErrorBoundaryOnlyInProdutionMode = props => {
  const { children } = props;
  return appSettings.dev ? (
    children
  ) : (
    <LoadableComponentErrorBoundary>{children}</LoadableComponentErrorBoundary>
  );
};
export default UseLoadableErrorBoundaryOnlyInProdutionMode;
